import {noop} from '@benchling/js-utils';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ReactFlowProvider} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {
  FEATURE_OVERRIDES_FOR_EDITOR_MODE,
  FlowchartEditorMode,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import FlowchartSidepanelManager from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelManager';
import {capitalize} from 'util/NameUtil';

import {AllConfigurableTaskDefinitionsQuery} from '../../__generated/AllConfigurableTaskDefinitionsQuery';
import ProcessFlowchartConfigEditorContent from '../../ProcessFlowchartConfigEditorContent';
import {FlowchartNode} from '../../processFlowchartConfigEditorNodeTypes';
import {FlowchartConfigWithMetadata} from '../../redux/flowchartConfigAutosaveManagerCallbacks';
import {FlowchartConfigMetadata} from '../../redux/reduceFlowchartConfig';
import {SingletonTaskSchemasContext, SingletonTaskSchemasByName} from '../../SingletonTaskSchemasContext';
import {buildTransitionIndexByPTDVPair} from '../../types/transition/transitionKeyUtils';
import {TransitionsContextProvider, TransitionsContextValue} from '../../types/transition/TransitionsContext';
import {Transition} from '../../types/transition/TransitionTypes';
import {
  ALL_CONFIGURABLE_TASK_DEFINITIONS_QUERY,
  ConfigurableTaskDefinitionGQL,
  isConfigurableTaskDefinition,
} from '../../useAllSchemasById';
import {
  FlowchartConfigWithPartialMetadata,
  LiveStateChanges,
} from '../../useFlowchartState/BaseFlowchartConfigStateManager';
import {validateFlowchartData} from '../../useFlowchartValidationAndAutosaverControl';
import buildStaticFlowchartFromTransition, {
  buildRootNodeStaticData,
} from './buildStaticFlowchartFromTransition';
import extractTransitionFromDraftState, {
  extractChangedNodeData,
  extractUpdatedNodeDataByPTDVId,
  mergeFromDraftTransitions,
  TransitionModalResult,
  UpdatedNodeDataByPTDVId,
} from './extractTransitionFromDraftState';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';

export enum TransitionModalAction {
  EDIT = 'EDIT',
  VIEW = 'VIEW',
  ADD = 'ADD',
}

export enum TransitionType {
  PLANNED = 'PLANNED',
  OPTIONAL = 'OPTIONAL',
}

interface TransitionMode {
  action: TransitionModalAction;
  type: TransitionType;
}

export interface SingleFlowchartTransitionModalProps {
  action: TransitionModalAction;
  /** The transition to view/edit, undefined for add mode. */
  transition?: Transition;
  /** All transitions, used by validation to detect duplicates. */
  allTransitions: Record<string, Transition>;
  ptdvIdToTaskSchemaId: Record<string, string>;
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  allSchemasById: SchemaById;
  flowchartTaskDefinitionId: string;
  flowchartConfigId: string;
  singletonTaskSchemasByName: SingletonTaskSchemasByName;
  onRequestClose: () => void;
  onRequestSubmit: (result: TransitionModalResult) => void;
  /** Called in real-time when schema-driven node data changes (e.g. renames) are detected, without dirtying the flowchart. */
  onSchemaNodeDataUpdate?: (updatedNodeDataByPTDVId: UpdatedNodeDataByPTDVId) => void;
}

const NOOP_LIVE_STATE_CHANGES: LiveStateChanges = {
  dirtyState: [false, noop],
  readonlyState: [true, noop],
  isSaveInFlightState: [false, noop],
  isAutosaverPausedState: [false, noop],
  isAutosaverBlockedState: [false, noop],
};

function applyPartialUpdate(
  current: FlowchartConfigWithMetadata,
  partial: FlowchartConfigWithPartialMetadata
): FlowchartConfigWithMetadata {
  const updatedStaticFlowchart = partial.staticFlowchart ?? current.staticFlowchart;
  let updatedMetadata = current.metadata;
  if (partial.flowchartConfigMetadata != null) {
    const definedEntries = Object.entries(partial.flowchartConfigMetadata).filter(
      ([, value]) => value !== undefined
    );
    updatedMetadata = {
      ...current.metadata,
      ...Object.fromEntries(definedEntries),
    };
  }
  return {staticFlowchart: updatedStaticFlowchart, metadata: updatedMetadata};
}

function getModalTitle(mode: TransitionMode): string {
  const {action, type} = mode;
  const title = capitalize(action);
  return `${title} ${type.toLowerCase()} transition`;
}

interface TransitionModalContentProps {
  action: TransitionModalAction;
  transition?: Transition;
  allTransitions: Record<string, Transition>;
  ptdvIdToTaskSchemaId: Record<string, string>;
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  allSchemasById: SchemaById;
  flowchartTaskDefinitionId: string;
  flowchartConfigId: string;
  singletonTaskSchemasByName: SingletonTaskSchemasByName;
  draftStateRef: React.MutableRefObject<FlowchartConfigWithMetadata | null>;
  onBlockingErrorsChange: (hasBlockingErrors: boolean) => void;
  onSchemaNodeDataUpdate?: (updatedNodeDataByPTDVId: UpdatedNodeDataByPTDVId) => void;
}

const PLANNED_TRANSITION_DISABLED_FEATURES: ReadonlyArray<ProcessFlowchartPlatformFeatureType> = [
  ProcessFlowchartPlatformFeatureType.SHOW_ADD_NODE_SIDEPANEL,
];

/**
 * Builds a configurator for the single-transition modal by applying feature overrides
 * defined in FEATURE_OVERRIDES_FOR_EDITOR_MODE for the SINGLE_TRANSITION_CANVAS mode.
 */
function buildSingleTransitionConfigurator(
  base: ProcessFlowchartEditorConfigurator,
  isPlanned: boolean
): ProcessFlowchartEditorConfigurator {
  const features = new Map(base.platformFlowchartFeatures);
  const overrides = FEATURE_OVERRIDES_FOR_EDITOR_MODE[FlowchartEditorMode.SINGLE_TRANSITION_CANVAS];
  for (const platformFeature of Object.values(ProcessFlowchartPlatformFeatureType)) {
    const override = overrides[platformFeature];
    if (override != null) {
      features.set(platformFeature, override);
    }
  }
  if (isPlanned) {
    for (const feature of PLANNED_TRANSITION_DISABLED_FEATURES) {
      features.set(feature, false);
    }
  }
  return new ProcessFlowchartEditorConfigurator(
    base.flowchartTypeName,
    base.allowedNodeExecutionTypes,
    features
  );
}

function TransitionModalContent(props: TransitionModalContentProps): JSX.Element {
  const {
    action,
    transition,
    allTransitions,
    ptdvIdToTaskSchemaId,
    taskDefinitionsForSidepanel,
    rootTaskSchema,
    flowchartEditorConfigurator,
    allSchemasById,
    flowchartTaskDefinitionId,
    flowchartConfigId,
    singletonTaskSchemasByName,
    draftStateRef,
    onBlockingErrorsChange,
    onSchemaNodeDataUpdate,
  } = props;

  const isLocked = action === TransitionModalAction.VIEW;

  const processTaskDefinitionsResult = useBenchlingQuery<AllConfigurableTaskDefinitionsQuery>(
    ALL_CONFIGURABLE_TASK_DEFINITIONS_QUERY
  );

  const liveTaskDefinitionsForSidepanel = useMemo(() => {
    if (processTaskDefinitionsResult.type !== QueryResultType.SUCCESS) {
      return taskDefinitionsForSidepanel;
    }
    const nodeExecutionTypes = flowchartEditorConfigurator.allowedNodeExecutionTypes;
    return processTaskDefinitionsResult.data.processTaskDefinitionsSupportingPlatformFeature
      .filter((definition) => isConfigurableTaskDefinition(definition))
      .filter((definition) =>
        nodeExecutionTypes.includes(definition.latestVersion.processTaskSchema.executionType)
      );
  }, [processTaskDefinitionsResult, flowchartEditorConfigurator, taskDefinitionsForSidepanel]);

  const liveAllSchemasById = useMemo(() => {
    if (processTaskDefinitionsResult.type !== QueryResultType.SUCCESS) {
      return allSchemasById;
    }
    const updated: SchemaById = {...allSchemasById};
    for (const definition of processTaskDefinitionsResult.data
      .processTaskDefinitionsSupportingPlatformFeature) {
      if (isConfigurableTaskDefinition(definition)) {
        const taskSchema = definition.latestVersion.processTaskSchema;
        updated[taskSchema.id] = taskSchema;
        if (taskSchema.outputSchema != null) {
          updated[taskSchema.outputSchema.id] = taskSchema.outputSchema;
        }
      }
    }
    return updated;
  }, [allSchemasById, processTaskDefinitionsResult]);

  const liveStateChanges: LiveStateChanges = useMemo(
    () => ({
      ...NOOP_LIVE_STATE_CHANGES,
      readonlyState: [isLocked, noop],
    }),
    [isLocked]
  );

  const isPlanned = transition?.isPlanned === true;

  const singleTransitionConfigurator = useMemo(
    () => buildSingleTransitionConfigurator(flowchartEditorConfigurator, isPlanned),
    [flowchartEditorConfigurator, isPlanned]
  );

  const getNodeByIdRef = useRef<(nodeId: string) => FlowchartNode | null>(() => null);
  const getNodeNameById = useCallback(
    (nodeId: string): string | null => getNodeByIdRef.current(nodeId)?.data.nodeName ?? null,
    []
  );

  const rootNodeStaticData = useMemo(
    () => buildRootNodeStaticData(rootTaskSchema, taskDefinitionsForSidepanel),
    [rootTaskSchema, taskDefinitionsForSidepanel]
  );

  const [draftFlowchartConfigAndMetadata, setDraftState] = useState<FlowchartConfigWithMetadata>(() => {
    const initial: FlowchartConfigWithMetadata = {
      staticFlowchart:
        transition != null
          ? buildStaticFlowchartFromTransition({
              transition,
              ptdvIdToTaskSchemaId,
              taskDefinitionsForSidepanel,
              rootTaskSchema,
              rootNodeStaticData,
            })
          : {nodesStaticData: [], edgesStaticData: []},
      metadata: {
        transitions: transition != null ? {[transition.id]: transition} : {},
        rootTaskSchema,
        rootNodeStaticData,
        interactionState: {
          selectedNodeIds: new Set<string>(),
          selectedRowIds: new Set<string>(),
          selectedEdgeIds: new Set<string>(),
          edgeLabelsByEdgeId: {},
        },
        currentVersionId: null,
        didInitialFlowchartHaveChainedRouters: false,
        flowchartTypeName: singleTransitionConfigurator.flowchartTypeName,
        options: {
          isFlowchartModifiableOnCreation: true,
          isFlowchartModifiableAfterCreation: false,
        },
      },
    };
    draftStateRef.current = initial;
    return initial;
  });

  const setDraftFlowchartConfigAndMetadata = useCallback(
    (
      updater: (current: FlowchartConfigWithMetadata) => FlowchartConfigWithPartialMetadata | null,
      _saveImmediately?: boolean,
      _skipSettingDirtyState?: boolean
    ): Promise<FlowchartConfigMetadata | null> => {
      const current = draftStateRef.current!;
      const partial = updater(current);
      if (partial == null) {
        return Promise.resolve(null);
      }
      const next = applyPartialUpdate(current, partial);
      setDraftState(next);
      draftStateRef.current = next;
      return Promise.resolve(next.metadata);
    },
    [draftStateRef]
  );

  const plugin = useMemo(
    () => PLUGIN_REGISTRY_INSTANCE.getPluginForFlowchartType(singleTransitionConfigurator.flowchartTypeName),
    [singleTransitionConfigurator.flowchartTypeName]
  );

  const fallbackFlowchartId = useMemo(() => uuidv4(), []);

  // The modal internally tracks multiple per-edge draft transitions (e.g. {A}->B and {C}->B).
  // We merge these into a single transition so validation sees consolidated lookup configs
  // and transition sources.
  const {transitionsForValidation, validationFlowchartId} = useMemo(() => {
    const draftTransitions = Object.values(draftFlowchartConfigAndMetadata.metadata.transitions);
    const transitionId = transition?.id ?? fallbackFlowchartId;
    if (draftTransitions.length === 0) {
      return {
        transitionsForValidation: allTransitions,
        validationFlowchartId: transitionId,
      };
    }
    const merged = mergeFromDraftTransitions(draftTransitions, transition);
    return {
      transitionsForValidation: {...allTransitions, [transitionId]: {...merged, id: transitionId}},
      validationFlowchartId: transitionId,
    };
  }, [allTransitions, draftFlowchartConfigAndMetadata.metadata.transitions, transition, fallbackFlowchartId]);
  const validationErrors: Array<ProcessFlowchartValidationError> = useMemo(
    () =>
      validateFlowchartData(
        draftFlowchartConfigAndMetadata.staticFlowchart,
        validationFlowchartId,
        {
          ...draftFlowchartConfigAndMetadata.metadata,
          transitions: transitionsForValidation,
        },
        rootTaskSchema,
        false,
        plugin,
        undefined,
        FlowchartEditorMode.SINGLE_TRANSITION_CANVAS
      ),
    [draftFlowchartConfigAndMetadata, validationFlowchartId, transitionsForValidation, rootTaskSchema, plugin]
  );

  const hasBlockingErrors = useMemo(
    () => validationErrors.some((error) => error.blocksPersistence),
    [validationErrors]
  );

  useEffect(() => {
    onBlockingErrorsChange(hasBlockingErrors);
  }, [hasBlockingErrors, onBlockingErrorsChange]);

  // Push schema-driven node data changes (e.g. renames) to the parent canvas in real-time,
  // rather than waiting for modal close. This ties the update to the schema change itself.
  const lastPushedNodeDataRef = useRef<UpdatedNodeDataByPTDVId | null>(null);
  useEffect(() => {
    if (onSchemaNodeDataUpdate == null) {
      return;
    }

    const currentNodeData = extractUpdatedNodeDataByPTDVId(draftFlowchartConfigAndMetadata);
    const baseline = lastPushedNodeDataRef.current;
    if (baseline == null) {
      lastPushedNodeDataRef.current = currentNodeData;
      return;
    }

    const changedNodeData = extractChangedNodeData(baseline, currentNodeData);
    if (Object.keys(changedNodeData).length > 0) {
      onSchemaNodeDataUpdate(changedNodeData);
      lastPushedNodeDataRef.current = currentNodeData;
    }
  }, [draftFlowchartConfigAndMetadata, onSchemaNodeDataUpdate]);

  const transitionsContextValue: TransitionsContextValue = useMemo(() => {
    const transitionsById = draftFlowchartConfigAndMetadata.metadata.transitions;
    return {
      transitionsById,
      ptdvPairToTransitionIdIndex: buildTransitionIndexByPTDVPair(transitionsById),
    };
  }, [draftFlowchartConfigAndMetadata.metadata.transitions]);

  return (
    <SingletonTaskSchemasContext.Provider value={singletonTaskSchemasByName}>
      <ReactFlowLockedContext.Provider value={isLocked}>
        <FlowchartSidepanelManager
          getNodeNameById={getNodeNameById}
          flowchartType={singleTransitionConfigurator.flowchartTypeName}
        >
          <TransitionsContextProvider value={transitionsContextValue}>
            <ProcessFlowchartConfigEditorContent
              getNodeByIdRef={getNodeByIdRef}
              flowchart={draftFlowchartConfigAndMetadata.staticFlowchart}
              flowchartEditorConfigurator={singleTransitionConfigurator}
              editorMode={FlowchartEditorMode.SINGLE_TRANSITION_CANVAS}
              allSchemasById={liveAllSchemasById}
              rootTaskSchema={rootTaskSchema}
              flowchartTaskDefinitionId={flowchartTaskDefinitionId}
              flowchartConfigId={flowchartConfigId}
              taskDefinitionsResult={
                processTaskDefinitionsResult.type === QueryResultType.PENDING &&
                liveTaskDefinitionsForSidepanel.length === 0
                  ? null
                  : liveTaskDefinitionsForSidepanel
              }
              didInitialConfigHaveConditionalMapping={false}
              didInitialFlowchartHaveChainedRouters={false}
              liveStateChanges={liveStateChanges}
              locked={isLocked}
              validationErrors={validationErrors}
              draftFlowchartConfigAndMetadata={draftFlowchartConfigAndMetadata}
              setDraftFlowchartConfigAndMetadata={setDraftFlowchartConfigAndMetadata}
              initiallyExpandAddNodeTab={!isLocked && !isPlanned}
            />
          </TransitionsContextProvider>
        </FlowchartSidepanelManager>
      </ReactFlowLockedContext.Provider>
    </SingletonTaskSchemasContext.Provider>
  );
}

/**
 * A modal for editing or viewing a single flowchart transition.
 *
 * Supports default & optional transitions, and merging & single node transitions.
 */
export default function SingleFlowchartTransitionModal(
  props: SingleFlowchartTransitionModalProps
): JSX.Element {
  const {
    action,
    transition,
    allTransitions,
    ptdvIdToTaskSchemaId,
    taskDefinitionsForSidepanel,
    rootTaskSchema,
    flowchartEditorConfigurator,
    allSchemasById,
    flowchartTaskDefinitionId,
    flowchartConfigId,
    singletonTaskSchemasByName,
    onRequestClose,
    onRequestSubmit,
    onSchemaNodeDataUpdate,
  } = props;
  const type = transition?.isPlanned === true ? TransitionType.PLANNED : TransitionType.OPTIONAL;
  const mode: TransitionMode = {action, type};
  const isViewMode = action === TransitionModalAction.VIEW;

  const draftStateRef = useRef<FlowchartConfigWithMetadata | null>(null);
  const [hasBlockingErrors, setHasBlockingErrors] = useState(false);

  const handleSubmit = useCallback(() => {
    if (draftStateRef.current == null || hasBlockingErrors) {
      return;
    }
    const updatedTransition = extractTransitionFromDraftState(draftStateRef.current, transition);
    if (updatedTransition != null) {
      const updatedNodeDataByPTDVId = extractUpdatedNodeDataByPTDVId(draftStateRef.current);
      onRequestSubmit({transition: updatedTransition, updatedNodeDataByPTDVId});
    }
  }, [onRequestSubmit, transition, hasBlockingErrors]);

  const buttons = isViewMode
    ? [
        {
          text: 'Close',
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: onRequestClose,
        },
      ]
    : [
        {
          text: 'Cancel',
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: onRequestClose,
        },
        {
          text: 'Save',
          style: ButtonStyle.PRIMARY,
          submit: true,
          disabled: hasBlockingErrors,
        },
      ];

  return (
    <Modal
      title={getModalTitle(mode)}
      extraLarge={true}
      fullHeight={true}
      bodyPadded={false}
      flexContent={true}
      onRequestClose={onRequestClose}
      onRequestSubmit={isViewMode ? undefined : handleSubmit}
      buttons={buttons}
      dataTestAttrs={{component: 'TransitionModal', element: 'root'}}
    >
      <ReactFlowProvider>
        <TransitionModalContent
          action={action}
          transition={transition}
          allTransitions={allTransitions}
          ptdvIdToTaskSchemaId={ptdvIdToTaskSchemaId}
          taskDefinitionsForSidepanel={taskDefinitionsForSidepanel}
          rootTaskSchema={rootTaskSchema}
          flowchartEditorConfigurator={flowchartEditorConfigurator}
          allSchemasById={allSchemasById}
          flowchartTaskDefinitionId={flowchartTaskDefinitionId}
          flowchartConfigId={flowchartConfigId}
          singletonTaskSchemasByName={singletonTaskSchemasByName}
          draftStateRef={draftStateRef}
          onBlockingErrorsChange={setHasBlockingErrors}
          onSchemaNodeDataUpdate={onSchemaNodeDataUpdate}
        />
      </ReactFlowProvider>
    </Modal>
  );
}
